// BaseFlow Query Builder - Supabase-like query interface

import { BaseFlowClient } from './client';
import { 
  BaseFlowResponse, 
  PostgrestBuilder as IPostgrestBuilder, 
  PostgrestFilterBuilder as IPostgrestFilterBuilder, 
  PostgrestQueryBuilder as IPostgrestQueryBuilder 
} from './types';

/**
 * A thenable class that represents a PostgREST request.
 */
export class PostgrestBuilder<T = any> implements IPostgrestBuilder<T> {
  protected client: BaseFlowClient;
  protected table: string;
  protected method: string = 'GET';
  protected path: string;
  protected params: Record<string, any> = {};
  protected body: any = null;
  protected headers: Record<string, string> = {};

  constructor(client: BaseFlowClient, table: string) {
    this.client = client;
    this.table = table;
    this.path = `/rest/v1/${table}`;
  }

  /**
   * Executes the request and returns a promise that resolves with the response.
   *
   * @param onfulfilled A function to be called when the promise is fulfilled.
   * @param onrejected A function to be called when the promise is rejected.
   * @returns A promise that resolves with the response.
   */
  async then<TResult1 = BaseFlowResponse<T>, TResult2 = never>(
    onfulfilled?: ((value: BaseFlowResponse<T>) => TResult1 | PromiseLike<TResult1>) | undefined | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
  ): Promise<TResult1 | TResult2> {
    try {
      const response = await this.client.request(this.method, this.path, {
        params: this.params,
        body: this.body,
        headers: this.headers
      });
      
      if (onfulfilled) {
        return onfulfilled(response);
      }
      return response as any;
    } catch (error) {
      if (onrejected) {
        return onrejected(error);
      }
      throw error;
    }
  }
}

/**
 * A builder for creating PostgREST filters.
 */
export class PostgrestFilterBuilder<T = any> extends PostgrestBuilder<T> implements IPostgrestFilterBuilder<T> {
  
  /**
   * Adds an 'equals' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  eq(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `${String(column)}=${this.escapeValue(value)}`);
    return this;
  }

  /**
   * Adds a 'not equals' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  neq(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `${String(column)}<>${this.escapeValue(value)}`);
    return this;
  }

  /**
   * Adds a 'greater than' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  gt(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `${String(column)}>${this.escapeValue(value)}`);
    return this;
  }

  /**
   * Adds a 'greater than or equal to' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  gte(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `${String(column)}>=${this.escapeValue(value)}`);
    return this;
  }

  /**
   * Adds a 'less than' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  lt(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `${String(column)}<${this.escapeValue(value)}`);
    return this;
  }

  /**
   * Adds a 'less than or equal to' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  lte(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `${String(column)}<=${this.escapeValue(value)}`);
    return this;
  }

  /**
   * Adds a 'like' filter to the query.
   *
   * @param column The column to filter on.
   * @param pattern The pattern to match.
   * @returns The filter builder.
   */
  like(column: keyof T, pattern: string): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `${String(column)} LIKE ${this.escapeValue(pattern)}`);
    return this;
  }

  /**
   * Adds a 'case-insensitive like' filter to the query.
   *
   * @param column The column to filter on.
   * @param pattern The pattern to match.
   * @returns The filter builder.
   */
  ilike(column: keyof T, pattern: string): PostgrestFilterBuilder<T> {
    // SQLite doesn't have ILIKE, use LIKE with LOWER
    this.params.where = this.addFilter(this.params.where, `LOWER(${String(column)}) LIKE LOWER(${this.escapeValue(pattern)})`);
    return this;
  }

  /**
   * Adds an 'is' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  is(column: keyof T, value: null | boolean): PostgrestFilterBuilder<T> {
    if (value === null) {
      this.params.where = this.addFilter(this.params.where, `${String(column)} IS NULL`);
    } else {
      this.params.where = this.addFilter(this.params.where, `${String(column)}=${this.escapeValue(value)}`);
    }
    return this;
  }

  /**
   * Adds an 'in' filter to the query.
   *
   * @param column The column to filter on.
   * @param values The values to filter by.
   * @returns The filter builder.
   */
  in(column: keyof T, values: any[]): PostgrestFilterBuilder<T> {
    const escapedValues = values.map(v => this.escapeValue(v)).join(',');
    this.params.where = this.addFilter(this.params.where, `${String(column)} IN (${escapedValues})`);
    return this;
  }

  /**
   * Adds a 'contains' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  contains(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    // For JSON contains - simplified for SQLite
    this.params.where = this.addFilter(this.params.where, `${String(column)} LIKE ${this.escapeValue(`%${value}%`)}`);
    return this;
  }

  /**
   * Adds a 'contained by' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  containedBy(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    // Simplified implementation
    return this.contains(column, value);
  }

  /**
   * Adds a 'range greater than' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  rangeGt(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    return this.gt(column, value);
  }

  /**
   * Adds a 'range greater than or equal to' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  rangeGte(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    return this.gte(column, value);
  }

  /**
   * Adds a 'range less than' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  rangeLt(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    return this.lt(column, value);
  }

  /**
   * Adds a 'range less than or equal to' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  rangeLte(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    return this.lte(column, value);
  }

  /**
   * Adds a 'range adjacent' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  rangeAdjacent(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    // Simplified - not directly supported in SQLite
    return this.eq(column, value);
  }

  /**
   * Adds an 'overlaps' filter to the query.
   *
   * @param column The column to filter on.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  overlaps(column: keyof T, value: any): PostgrestFilterBuilder<T> {
    // Simplified implementation
    return this.contains(column, value);
  }

  /**
   * Adds a 'text search' filter to the query.
   *
   * @param column The column to filter on.
   * @param query The query to search for.
   * @returns The filter builder.
   */
  textSearch(column: keyof T, query: string): PostgrestFilterBuilder<T> {
    // Use LIKE for text search in SQLite
    this.params.where = this.addFilter(this.params.where, `${String(column)} LIKE ${this.escapeValue(`%${query}%`)}`);
    return this;
  }

  /**
   * Adds a 'match' filter to the query.
   *
   * @param query The query to match.
   * @returns The filter builder.
   */
  match(query: Record<keyof T, any>): PostgrestFilterBuilder<T> {
    Object.entries(query).forEach(([key, value]) => {
      this.eq(key as keyof T, value);
    });
    return this;
  }

  /**
   * Adds a 'not' filter to the query.
   *
   * @param column The column to filter on.
   * @param operator The operator to use.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  not(column: keyof T, operator: string, value: any): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `NOT (${String(column)} ${operator} ${this.escapeValue(value)})`);
    return this;
  }

  /**
   * Adds an 'or' filter to the query.
   *
   * @param filters The filters to apply.
   * @returns The filter builder.
   */
  or(filters: string): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `(${filters})`, 'OR');
    return this;
  }

  /**
   * Adds a filter to the query.
   *
   * @param column The column to filter on.
   * @param operator The operator to use.
   * @param value The value to filter by.
   * @returns The filter builder.
   */
  filter(column: keyof T, operator: string, value: any): PostgrestFilterBuilder<T> {
    this.params.where = this.addFilter(this.params.where, `${String(column)} ${operator} ${this.escapeValue(value)}`);
    return this;
  }

  protected addFilter(existing: string | undefined, newFilter: string, operator: string = 'AND'): string {
    if (!existing) {
      return newFilter;
    }
    return `${existing} ${operator} ${newFilter}`;
  }

  protected escapeValue(value: any): string {
    if (value === null) return 'NULL';
    if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`;
    if (typeof value === 'boolean') return value ? '1' : '0';
    return String(value);
  }
}

/**
 * A builder for creating PostgREST queries.
 */
export class PostgrestQueryBuilder<T = any> extends PostgrestFilterBuilder<T> implements IPostgrestQueryBuilder<T> {
  
  /**
   * Specifies the columns to select.
   * Supports Supabase-like syntax with JOINs and nested selections.
   * 
   * Examples:
   * - select('*') - Select all columns
   * - select('name, email') - Select specific columns
   * - select('title, author:users(name, email)') - JOIN with users table
   * - select('title, comments(count)') - Aggregate count of comments
   *
   * @param columns The columns to select.
   * @returns The query builder.
   */
  select(columns: string = '*'): PostgrestQueryBuilder<T> {
    this.params.select = columns;
    return this;
  }

  /**
   * Inserts data into the table.
   *
   * @param data The data to insert.
   * @returns A promise that resolves with the response.
   */
  insert(data: Partial<T> | Partial<T>[]): PostgrestBuilder<T> {
    this.method = 'POST';
    this.body = data;
    return this;
  }

  /**
   * Updates data in the table.
   *
   * @param data The data to update.
   * @returns A filter builder for specifying which rows to update.
   */
  update(data: Partial<T>): PostgrestFilterBuilder<T> {
    this.method = 'PATCH';
    this.body = data;
    return this;
  }

  /**
   * Deletes data from the table.
   *
   * @returns A filter builder for specifying which rows to delete.
   */
  delete(): PostgrestFilterBuilder<T> {
    this.method = 'DELETE';
    return this;
  }

  /**
   * Adds an 'order by' clause to the query.
   *
   * @param column The column to order by.
   * @param options The ordering options.
   * @returns The query builder.
   */
  order(column: keyof T, options: { ascending?: boolean; nullsFirst?: boolean } = {}): PostgrestQueryBuilder<T> {
    const direction = options.ascending !== false ? 'ASC' : 'DESC';
    const nullsOrder = options.nullsFirst ? 'NULLS FIRST' : 'NULLS LAST';
    this.params.order = `${String(column)} ${direction} ${nullsOrder}`;
    return this;
  }

  /**
   * Limits the number of rows returned.
   *
   * @param count The maximum number of rows to return.
   * @returns The query builder.
   */
  limit(count: number): PostgrestQueryBuilder<T> {
    this.params.limit = count;
    return this;
  }

  /**
   * Specifies a range of rows to return.
   *
   * @param from The starting row index.
   * @param to The ending row index.
   * @returns The query builder.
   */
  range(from: number, to: number): PostgrestQueryBuilder<T> {
    this.params.limit = to - from + 1;
    this.params.offset = from;
    return this;
  }

  /**
   * Returns a single row from the query.
   *
   * @returns The filter builder.
   */
  single(): PostgrestFilterBuilder<T> {
    this.params.limit = 1;
    return this;
  }

  /**
   * Returns a single row from the query, or null if no rows are found.
   *
   * @returns The filter builder.
   */
  maybeSingle(): PostgrestFilterBuilder<T> {
    this.params.limit = 1;
    return this;
  }

  /**
   * Subscribe to real-time changes on this table
   *
   * @param event The event type to listen for
   * @param callback The callback function to execute when changes occur
   * @returns The subscription object
   */
  on(event: 'INSERT' | 'UPDATE' | 'DELETE' | '*', callback: (payload: any) => void): RealtimeSubscriptionBuilder<T> {
    return new RealtimeSubscriptionBuilder<T>(this.client, this.table, event, callback, this.params);
  }
}

/**
 * Real-time subscription builder
 */
export class RealtimeSubscriptionBuilder<T = any> {
  private client: BaseFlowClient;
  private table: string;
  private event: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  private callback: (payload: any) => void;
  private filters: Record<string, any>;

  constructor(
    client: BaseFlowClient, 
    table: string, 
    event: 'INSERT' | 'UPDATE' | 'DELETE' | '*', 
    callback: (payload: any) => void,
    filters: Record<string, any> = {}
  ) {
    this.client = client;
    this.table = table;
    this.event = event;
    this.callback = callback;
    this.filters = filters;
  }
}